import { useCallback, useMemo, useRef, useState } from 'react';
import type { FiltersGroups } from '@/app/common/filters/core/filtersTypes.ts';
import type { FilterSessionInput } from '@/app/common/filters/session/selections.ts';
import type { FilterOrder } from '@/app/common/filters/session/filterOrder.ts';
import { deriveFilterOrder } from '@/app/common/filters/session/filterOrder.ts';
import type { FilterSessionState } from '@/app/common/filters/session/sessionState.ts';
import { nextFilterSession } from '@/app/common/filters/session/sessionUpdate.ts';
import { writeFilterSessionCookie } from '@/app/common/filters/session/sessionCookie.ts';

export type FilterSessionApi = {
  order: FilterOrder;
  session: FilterSessionState;
  recordSelections: (input: FilterSessionInput) => void;
};

export const useFilterSession = (
  filtersGroups: FiltersGroups,
  filterSession: FilterSessionState,
  enabled: boolean
): FilterSessionApi => {
  const [session, setSession] = useState<FilterSessionState>(filterSession);
  const sessionRef = useRef(session);
  const order = useMemo(() => deriveFilterOrder(session, filtersGroups), [session, filtersGroups]);

  const recordSelections = useCallback(
    ({ selections, justUsed, pressed }: FilterSessionInput) => {
      if (!enabled || !selections.length) return;

      const next = nextFilterSession(sessionRef.current, selections, justUsed, pressed);
      sessionRef.current = next;
      setSession(next);
      writeFilterSessionCookie(next);
    },
    [enabled]
  );

  return { order, session, recordSelections };
};
